// import PropTypes from "prop-types";
/*eslint-disable*/
import { useEffect } from "react";
import { LoaderCircle, Wrench, Speaker, Key, Zap, Cpu } from "lucide-react";
import { TEXT_LIBRARY } from "../../lib/utils/constants";
import ErrorCard from "../../lib/components/ErrorCard";
import { useGlobalFilters } from "../../lib/context/GlobalFilterProvider";
import useChargerConfig from "./hooks/useChargerConfig";
import { formatDate, getTimezoneOffset } from "../../lib/utils/utility";
import { chargerDetailsMap, fieldGroups } from "./constants";
import "./styles.scss";
import { useNavigate, useParams } from "react-router-dom";

const SummaryCard = ({ icon: Icon, label, value, statusColor, isBold = false }) => (
  <div className="summary-card">
    <div className={`icon-wrapper ${statusColor ? statusColor : ""}`}>
      <Icon size={20} />
    </div>
    <div className="card-content">
      <span className="card-label">{label}</span>
      <span className={`card-value ${statusColor ? "text-" + statusColor : ""} ${isBold ? "is-bold" : ""}`}>
        {value || "-"}
      </span>
    </div>
  </div>
);

const ActionButton = ({ label, onClick = () => {} }) => (
  <button className="action-btn" onClick={onClick}>
    {label}
  </button>
);

function EvSummary() {
  const { setState, summarySerialNumber, summarySiteId } = useGlobalFilters();
  const { config, loading, error } = useChargerConfig(summarySiteId, summarySerialNumber);
  const navigate = useNavigate();
  const { chargerId } = useParams();

  useEffect(() => {
    if (chargerId) {
      setState({ serialNumber: chargerId });
    }
  }, [chargerId]);

  const dateKeys = new Set(["lastReportedAt", "createdAt", "warrantyDueDate", "warrantyStartDate", "updatedAt"]);

  function formatValue(key, value) {
    if (key === "timezone") {
      return `${value} (${getTimezoneOffset(value)})`;
    }
    if (key === "phaseMode") {
      // using loose equality (==) to handle both string "1" and number 1
      if (value == 1 || value == 3) return "IT";
      if (value == 2 || value == 4) return "TN/TT";
      return value || "-";
    }
    if (key === "hoControl") {
      return !value ? "Yes" : "No";
    }
    if (dateKeys.has(key)) return formatDate(value);
    if (typeof value === "boolean") return value ? "Yes" : "No";
    // specific check for 0 or valid falsey numbers if needed, otherwise:
    return value !== null && value !== undefined ? String(value) : "-";
  }

  function onBack() {
    navigate(-1);
  }

  function onChargerMetricsClick() {
    setState({ serialNumber: summarySerialNumber, siteId: summarySiteId });
    navigate("/charger");
  }

  function onOCPPMessagesClick() {
    setState({ serialNumber: summarySerialNumber, siteId: summarySiteId });
    navigate("/ocpp");
  }

  function onTroubleshootClick() {
    setState({ serialNumber: summarySerialNumber, siteId: summarySiteId });
    navigate("/troubleshoot");
  }

  const getStatusColor = status => (status === "NORMAL" ? "green" : "red");

  function renderDashboard(data) {
    if (!data) return <div className="text-gray-500 p-4">No data available</div>;

    return (
      <div className="dashboard-container">
        {/* --- LEFT COLUMN: Info & Details --- */}
        <div className="main-panel">
          {/* 1. Top Summary Cards */}
          <div className="summary-row">
            <SummaryCard icon={Wrench} label="Site Id" value={data.siteId || "-"} />
            <SummaryCard icon={Speaker} label="Firmware Version" value={data.firmwareVersion || "-"} />
            <SummaryCard icon={Key} label="Serial Number" value={data.serialNumber} />
            <SummaryCard
              icon={Zap}
              label="Status"
              value={data.status}
              statusColor={getStatusColor(data.status)}
              isBold={true}
            />
          </div>

          {/* 2. Navigation Tabs */}
          <div className="tabs-header">
            <button className="tab-btn active">Details</button>
          </div>

          {/* 3. Details Content (Grouped) */}
          <div className="details-card">
            {fieldGroups.map(group => (
              <div key={group.title} className="details-group mb-6">
                {/* Optional: Add a subtle header for the group */}
                <h4 className="text-sm font-semibold text-gray-500 mb-3 uppercase tracking-wider border-b pb-1">
                  {group.title}
                </h4>

                <div className="technical-grid">
                  {group.keys.map(key => {
                    if (!chargerDetailsMap[key]) return null;

                    return (
                      <div key={key} className="grid-item">
                        <span className="grid-label">{chargerDetailsMap[key]}</span>
                        <span className="grid-value">{formatValue(key, data?.[key])}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* --- RIGHT COLUMN: Sidebar Actions --- */}
        <div className="sidebar-panel">
          <div className="sidebar-section">
            <div className="sidebar-header">
              <Cpu size={18} />
              <span>Diagnostics</span>
            </div>
            <div className="sidebar-actions">
              <ActionButton label="Charger Metrics" onClick={onChargerMetricsClick} />
              <ActionButton label="OCPP Messages" onClick={onOCPPMessagesClick} />
              <ActionButton label="Troubleshoot" onClick={onTroubleshootClick} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="ev-summary-wrapper">
      <div className="header-bar">
        <span className="text-2xl font-bold mb-2">EV Charger Summary</span>
        <button className="back-link" onClick={onBack}>
          ← Back
        </button>
      </div>
      <div className="content-area">
        {!error && loading && summarySiteId && (
          <div className="loading-state">
            <LoaderCircle className="animate-spin text-blue-500" size={38} />
            <span>Loading Details...</span>
          </div>
        )}
        {error && <ErrorCard errorText={TEXT_LIBRARY.SERVER_ERROR} />}
        {!summarySiteId && <ErrorCard errorText={TEXT_LIBRARY.NO_ACTIVE_SITEID_FOUND} />}
        {!loading && !error && renderDashboard(config)}
      </div>
    </div>
  );
}

EvSummary.propTypes = {};

export default EvSummary;
